import { PackageSecurityServerAssignmentGridView } from '../view/PackageSecurityServerAssignmentGridView';
import { templatePackageSecurityServerAssignmentGrid } from '../view/template';
import { PackageSecurityServerAssignmentGridRepository } from '../repository/PackageSecurityServerAssignmentGridRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { PackageSecurityServerAssignmentGridWebComponent } from '../component/PackageSecurityServerAssignmentGridWebComponent';
import {
  PackageSecurityServerAssignmentGridWebComponentSchema,
  SchemaSVA,
  DataGridItem,
  PackageSecurityServerAssignmentGridSchema,
  PackageSVAEvent,
} from '../types';
import { GridEvents } from '../../../../grid/types/common';
import { InlineRowActionsWebComponent } from '../../../../grid/uiElements/inlineRowActions';
import { State } from '../../../../grid/uiElements/inlineRowActions/types';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';

export class PackageSecurityServerAssignmentGridController extends BaseWebComponentController {
  public component: PackageSecurityServerAssignmentGridWebComponent;
  public dataRepository: PackageSecurityServerAssignmentGridRepository;
  public view: PackageSecurityServerAssignmentGridView;
  public viewConstructor: typeof PackageSecurityServerAssignmentGridView;
  public dataRepositoryConstructor: typeof PackageSecurityServerAssignmentGridRepository;

  public gridData: Array<SchemaSVA> = [];
  private dropdownData: Array<SchemaSVA>;
  private companyId: string;

  constructor(component: PackageSecurityServerAssignmentGridWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: PackageSecurityServerAssignmentGridView,
      viewTemplate: templatePackageSecurityServerAssignmentGrid,
      dataRepositoryConstructor: PackageSecurityServerAssignmentGridRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$searchInput,
        event: 'selectionChanged',
        callback: this.onSelectedSVAChanged.bind(this),
      },
      {
        element: this.view.dom.$addSVAButton,
        event: 'click',
        callback: this.addSVAToGrid.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: PackageSVAEvent.OnDeleteSVA,
        callback: this.deleteSVA.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: PackageSVAEvent.MoveUpSVA,
        callback: this.moveUpSVA.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: PackageSVAEvent.MoveDownSVA,
        callback: this.moveDownSVA.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.EDIT_ROW_SAVE,
        callback: this.onEditGridRowSave.bind(this),
      },
    ];
  }

  private onSelectedSVAChanged(ev: WebComponentEvent): void {
    const selectedItem: SchemaSVA = ev.detail.data;

    if (!this.isValidSVA(selectedItem.protectedEntityId)) {
      this.view.dom.$customHostnamesInput.value = '';
      this.view.dom.$customHostnamesInput.disabled = true;
      this.view.dom.$addSVAButton.disabled = true;
      return;
    }

    let SVACustomHostname = '';

    this.dropdownData.forEach(item => {
      if (item.protectedEntityId === selectedItem.protectedEntityId) {
        SVACustomHostname = item.customHostname;
      }
    });

    this.view.dom.$customHostnamesInput.value = SVACustomHostname;
    this.view.dom.$customHostnamesInput.disabled = false;
    this.view.dom.$addSVAButton.disabled = false;
  }

  private async deleteSVA(ev: WebComponentEvent): Promise<void> {
    const selectedItem: SchemaSVA = this.view.dom.$searchInput.selectedOption?.data as SchemaSVA;
    const SVAs = this.gridData;
    const rowIndex = ev.detail.rowData.item.priority - 1;
    SVAs.splice(rowIndex, 1);

    this.gridData = SVAs;

    await this.rerender();

    if (this.gridData.length === 0) {
      // show the warning below the grid
      toggleVisibility(this.view.dom.$gridBoxInfo, true);
      this.component.setGridValidity(false);
      this.view.dom.$securityServerAssignmentFormFieldValidation.validate();
    }

    if (selectedItem && ev.detail.rowData.item.protectedEntityId === selectedItem.protectedEntityId) {
      this.view.dom.$customHostnamesInput.value = selectedItem.customHostname ?? '';
      this.view.dom.$customHostnamesInput.disabled = false;
      this.view.dom.$addSVAButton.disabled = false;
    }
  }

  private async moveUpSVA(ev: WebComponentEvent): Promise<void> {
    const rowIndex = ev.detail.rowData.item.priority - 1;

    const SVAs = this.gridData;
    const elementsNo = SVAs.length;

    if (rowIndex === 0) {
      return;
    }

    SVAs[elementsNo] = SVAs[rowIndex];
    SVAs[rowIndex] = SVAs[rowIndex - 1];
    SVAs[rowIndex - 1] = SVAs[elementsNo];

    SVAs.splice(elementsNo, 1);

    this.gridData = SVAs;

    await this.rerender();
  }

  private async moveDownSVA(ev: WebComponentEvent): Promise<void> {
    const rowIndex = ev.detail.rowData.item.priority - 1;

    const SVAs = this.gridData;
    const elementsNo = SVAs.length;

    if (rowIndex === elementsNo) {
      return;
    }

    SVAs[elementsNo] = SVAs[rowIndex];
    SVAs[rowIndex] = SVAs[rowIndex + 1];
    SVAs[rowIndex + 1] = SVAs[elementsNo];

    SVAs.splice(elementsNo, 1);

    this.gridData = SVAs;

    await this.rerender();
  }

  private isValidSVA(SVAProtectedEntityId: string): boolean {
    const SVAs = this.gridData;

    let validSVA = true;
    SVAs.forEach(item => {
      if (item.protectedEntityId === SVAProtectedEntityId) {
        validSVA = false;
      }
    });

    return validSVA;
  }

  private async addSVAToGrid(): Promise<void> {
    const SVAs = this.gridData;
    const selectedItem: SchemaSVA = this.view.dom.$searchInput.selectedOption?.data as SchemaSVA;

    if (this.view.dom.$customHostnamesInput.value !== selectedItem.customHostname) {
      const itemToBeUpdated: SchemaSVA = selectedItem;

      itemToBeUpdated.customHostname = this.view.dom.$customHostnamesInput.value as string;
      await this.dataRepository.updateSVAMachines([itemToBeUpdated as any]);
    }

    const dataGridSVAItem = {} as DataGridItem;
    dataGridSVAItem._id = selectedItem._id;
    dataGridSVAItem.protectedEntityId = selectedItem.protectedEntityId;
    dataGridSVAItem.name = selectedItem.name;
    dataGridSVAItem.ip = selectedItem.ip;
    dataGridSVAItem.customHostname = this.view.dom.$customHostnamesInput.value as string;

    SVAs.push(dataGridSVAItem as SchemaSVA);

    this.gridData = SVAs;

    // disable button after add
    this.view.dom.$addSVAButton.disabled = true;
    this.view.dom.$customHostnamesInput.disabled = true;
    this.view.dom.$customHostnamesInput.value = '';

    // clear input value after add
    this.clearInputSelector(this.view.dom.$searchInput);

    await this.rerender(true);

    if (this.gridData.length === 1) {
      // hide the warning below the grid
      toggleVisibility(this.view.dom.$gridBoxInfo, false);
      this.component.setGridValidity(true);
      this.view.dom.$securityServerAssignmentFormFieldValidation.validate();
    }
  }

  private clearInputSelector(selector): void {
    selector.lastSelectedOption = null;
    selector.selectedOption = null;

    this.view.dom.$addSVAButton.disabled = true;
    this.view.dom.$customHostnamesInput.disabled = true;

    selector.clearInput();
  }

  private async onEditGridRowSave(event: WebComponentEvent): Promise<void> {
    const newCustomHostname = event.detail.newData.item.customHostname;
    const oldCustomHostname = event.detail.rowData.item.customHostname;
    const currentRowIndex = event.detail.rowData.index;
    const itemIndex = event.detail.rowData.item.priority - 1;

    this.gridData[itemIndex].customHostname = newCustomHostname;

    const inlineMenu = this.view.dom.$gridView.controller?.view.getRowInlineMenu(
      currentRowIndex + 2
    ) as InlineRowActionsWebComponent;

    if (newCustomHostname === oldCustomHostname) {
      inlineMenu.state = State.SUCCESS;
      return;
    }

    inlineMenu.state = State.LOADING;
    inlineMenu.disabled = true;

    const response = await this.dataRepository.updateSVAMachines([event.detail.newData.item]);

    this.updateDropdownSVA(event.detail.rowData.item._id, newCustomHostname);

    inlineMenu.disabled = false;
    // we need to wait for state property on inlineMenu to once again exist
    setTimeout(() => {
      inlineMenu.state = response ? State.SUCCESS : State.ERROR;
    }, 1000);
  }

  private updateDropdownSVA(svaId: string, newCustomHostname: string): void {
    for (const option of this.view.dom.$searchInput.options) {
      if ((option.data as SchemaSVA)._id === svaId) {
        (option.data as SchemaSVA).customHostname = newCustomHostname;
        break;
      }
    }
  }

  public async render(): Promise<void> {
    const result = await this.getDataForView();
    this.dropdownData = result.data;

    this.component.setGridValidity(this.gridData.length !== 0);
    toggleVisibility(this.view.dom.$gridBoxInfo, this.gridData.length === 0);

    this.view.dom.$securityServerAssignmentFormFieldValidation.validate();

    this.view.render({
      gridData: this.gridData,
      dropdownData: this.dropdownData,
      useEncryption: this.component.getUseEncryption(),
    } as PackageSecurityServerAssignmentGridWebComponentSchema);
  }

  private async rerender(refreshDropdown = false): Promise<void> {
    if (refreshDropdown) {
      const result = await this.getDataForView();
      this.dropdownData = result.data;
    }

    if (
      this.view.dom.$gridView.pageNumber > 1 &&
      this.gridData.length <= (this.view.dom.$gridView.pageNumber - 1) * this.view.dom.$gridView.pageSize
    ) {
      this.view.dom.$gridView.pageNumber -= 1;
    }

    this.view.render({
      gridData: this.gridData,
      dropdownData: this.dropdownData,
    } as PackageSecurityServerAssignmentGridWebComponentSchema);
  }

  private handleCompanyChange(): void {
    this.view.setDefaultData();
  }

  public async getDataForView(): Promise<PackageSecurityServerAssignmentGridSchema> {
    if (this.component.companyId === '' || this.component.companyId === 'undefined') {
      return {
        data: [],
      };
    }

    //clear grid data on company switch
    if (this.companyId && this.companyId !== this.component.companyId) {
      this.gridData = [];
    } else {
      //EDIT MODE
      if (this.component.getScanServers()) {
        this.gridData = this.component.getScanServers();
      }
    }

    this.companyId = this.component.companyId;
    return this.dataRepository.getDataForView(this.component.companyId);
  }

  public async showSecurityServerAssignmentSection(isVisible: boolean, companyId: string | undefined): Promise<void> {
    if (companyId && this.component.companyId !== companyId) {
      this.component.companyId = companyId ?? '';
      await this.render();
      this.handleCompanyChange();
    }

    toggleVisibility(this.view.dom.$securityServerAssignmentSubtitle, isVisible);
    toggleVisibility(this.component, isVisible);

    this.component.setVisibility(isVisible);
    this.component.handleServerAssignmentValidators(isVisible);
  }
}
